import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Plus, Pencil, Gauge, Trash2, Target, ArrowUpRight } from "lucide-react";
import {
    useBudgets,
    useReferences,
    useCreateBudget,
    useUpdateBudget,
    useDeleteBudget,
    useInstanceConfig,
    usePlan,
    useBudgetRecommendations,
} from "@/api/queries";
import { Modal } from "@/components/Modal";
import { Select } from "@/components/Select";
import { ErrorState } from "@/components/ErrorState";
import { useToast } from "@/components/Toast";
import { BudgetBar, BUDGET_STATUS_TEXT } from "@/components/BudgetBar";
import { RecommendationsSection, EnvelopesSection, ClassificationSection } from "@/components/AdaptiveBudgets";
import { formatAmount, cn, useDraftId } from "@/lib/utils";
import type {
    BudgetAllocation,
    BudgetCategoryProgress,
    BudgetTotalProgress,
    Category,
    PlanCeiling,
} from "@/api/types";

const MONTHS_NOM = [
    "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
    "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];
function monthLabel(ym: string): string {
    const [y, m] = ym.split("-").map(Number);
    return `${MONTHS_NOM[m - 1] ?? ""} ${y}`;
}

const eur = (x: number) => `${formatAmount(x, "EUR")} €`;

type ModalState =
    | { mode: "create-category"; categoryId?: string }   // categoryId — пред-выбор из «Без лимита»
    | { mode: "create-total" }
    | { mode: "edit"; budgetId: string; label: string; emoji: string | null; color: string | null; limit: number; isTotal: boolean; managedByPlan?: boolean }
    | null;

export function BudgetsPage() {
    const { data, isLoading, isError, refetch } = useBudgets();
    const refs = useReferences();
    const [modal, setModal] = useState<ModalState>(null);
    // FEATURE_ADAPTIVE_BUDGETS гасит только RBAR-надстройку (SPEC-023). Ручные
    // месячные лимиты SPEC-020 — база страницы и остаются при выключенном флаге.
    const { data: config } = useInstanceConfig();
    const rbarEnabled = config?.features?.adaptive_budgets !== false;

    const total = data?.total ?? null;
    const cats = data?.categories ?? [];
    // SPEC-058: пока задана цель накоплений, общий потолок задаёт план — карточка read-only,
    // а под ней разбивка: Σ лимитов, пул «прочее», нераспределено.
    const allocation = data?.allocation ?? null;
    // SPEC-059: адаптивные категории в режиме потолка ставит сверка — модалка предупреждает,
    // что ручная правка перезапишется (owner-решение: одна модель, закрепить = выключить «адапт.»).
    // Сверка пишет только при включённом RBAR (иначе helper отвечает disabled_by_flag); всё
    // остальное — warmup/infeasible/удержание текущего при α = 0 — сервер отдаёт одним флагом
    // `held` («сверка этот лимит не пишет»). Не выводим его из HOLD/alpha сами: HOLD означает
    // и «лимит уже такой», а alpha округлён — категорию с минимумом сочли бы удержанной.
    const recs = useBudgetRecommendations();
    const managedByPlan = useMemo(() => {
        const d = recs.data;
        if (!rbarEnabled || d?.mode !== "ceiling") return new Set<string>();
        return new Set(
            d.recommendations
                .filter(r => r.recommended_limit_eur != null && !r.held)
                .map(r => r.category_id),
        );
    }, [recs.data, rbarEnabled]);
    const planTotal = total !== null && total.source === "plan";

    // SPEC-057: категории под годовым конвертом плана из контура лимитов выведены —
    // предлагать на них месячный лимит нельзя (сервер и триггер D1 такой лимит отвергнут).
    const { data: plan, isLoading: planLoading, isFetching: planFetching, isError: planError, refetch: refetchPlan } = usePlan();
    const planCategories = useMemo(
        () => new Map((plan?.envelopes ?? []).map(e => [e.category_id, e])),
        [plan?.envelopes],
    );

    const unbudgeted = useMemo(() => {
        const budgeted = new Set(cats.map(c => c.category_id));
        return (refs.data?.categories ?? [])
            .filter(c => c.type === "expense" && c.is_active && !budgeted.has(c.id) && !planCategories.has(c.id))
            .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    }, [refs.data?.categories, cats, planCategories]);

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight">Бюджеты</h1>
                    <p className="text-muted-foreground mt-1">
                        Месячные лимиты трат по категориям в EUR{data?.month ? ` · ${monthLabel(data.month)}` : ""}. Сколько ещё можно потратить в этом месяце.
                    </p>
                </div>
                <button
                    onClick={() => setModal({ mode: "create-category" })}
                    disabled={unbudgeted.length === 0}
                    className="btn-primary px-4 py-2 self-start disabled:opacity-50"
                    title={unbudgeted.length === 0 ? "У всех активных категорий уже есть лимит" : undefined}
                >
                    <Plus className="h-4 w-4" /> Бюджет
                </button>
            </div>

            {isLoading && (
                <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => <div key={i} className="card h-16 animate-pulse bg-muted/40" />)}
                </div>
            )}

            {isError && <ErrorState onRetry={() => refetch()} label="Не удалось загрузить бюджеты" />}

            {!isLoading && !isError && (
                <>
                    {/* SPEC-023: рекомендации адаптивного лимита */}
                    {rbarEnabled && <RecommendationsSection />}

                    {/* Общий месячный потолок. Карточка плана живёт на числах /plan (остаток по
                        регулярным тратам) — пока их нет, бухгалтерский total подставлять нельзя:
                        это другой ряд, и на секунду-две владелец видел бы другой остаток.
                        /budgets и /plan — два независимых запроса, и между ними бывает разъезд:
                        · /plan ещё грузится или перечитывается без потолка (цель только что
                          задана) → скелет, а не кнопка «задать потолок» на секунду;
                        · /plan упал → ошибка с повтором;
                        · /plan ответил без потолка (цель снята, /budgets ещё со старым
                          `source: plan`) → это не ошибка: рисуем ручную ветку, как будет и после
                          перечитывания /budgets (budget_id под планом всегда null → кнопка). */}
                    {planTotal && (planLoading || (planFetching && !plan?.ceiling)) ? (
                        <div className="card h-28 animate-pulse bg-muted/40" data-testid="plan-total-skeleton" />
                    ) : planTotal && planError ? (
                        <ErrorState onRetry={() => refetchPlan()} label="Не удалось загрузить потолок плана" />
                    ) : planTotal && plan?.ceiling ? (
                        <PlanTotalCard total={total} allocation={allocation} ceiling={plan.ceiling} />
                    ) : total && total.budget_id ? (
                        <TotalCard total={total} onEdit={() => setModal({
                            mode: "edit", budgetId: total.budget_id!, label: "Общий потолок",
                            emoji: "🧮", color: null, limit: total.limit_eur, isTotal: true,
                        })} />
                    ) : (
                        <button
                            onClick={() => setModal({ mode: "create-total" })}
                            className="card w-full p-4 flex items-center gap-3 text-left hover:bg-secondary/30 transition-colors border-dashed"
                        >
                            <div className="h-9 w-9 rounded-lg grid place-items-center bg-secondary/60 text-muted-foreground shrink-0"><Gauge className="h-5 w-5" /></div>
                            <div>
                                <div className="font-medium">Задать общий потолок</div>
                                <div className="text-sm text-muted-foreground">Один лимит на все траты за месяц</div>
                            </div>
                        </button>
                    )}

                    {/* Категорийные бюджеты */}
                    {cats.length === 0 ? (
                        <div className="card p-8 text-center text-muted-foreground text-sm">
                            <Gauge className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            Бюджеты по категориям не заданы — установите лимит кнопкой «Бюджет».
                        </div>
                    ) : (
                        <div className="card divide-y overflow-hidden">
                            {cats.map(c => (
                                <CategoryRow
                                    key={c.budget_id} b={c}
                                    onEdit={() => setModal({
                                        mode: "edit", budgetId: c.budget_id, label: c.name,
                                        emoji: c.emoji, color: c.color, limit: c.limit_eur, isTotal: false,
                                        managedByPlan: managedByPlan.has(c.category_id),
                                    })}
                                />
                            ))}
                        </div>
                    )}

                    {/* SPEC-023: накопительные конверты для lumpy-категорий. Данные —
                        тот же RBAR-эндпоинт, что у рекомендаций, и в Mini App они уже
                        гаснут вместе с withBudgets (/v1/bootstrap), поэтому здесь тот же
                        флаг: выключенный раздел не должен светиться ни в одном канале. */}
                    {rbarEnabled && <EnvelopesSection />}

                    {/* SPEC-057: категории, отданные годовым конвертам плана */}
                    {planCategories.size > 0 && (
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">В плане · годовые конверты</div>
                            <div className="flex flex-wrap gap-2">
                                {[...planCategories.values()].map(e => (
                                    <Link
                                        key={e.id}
                                        to="/plan"
                                        className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm hover:bg-secondary/40 transition-colors"
                                        style={{ borderColor: (e.color ?? "#94a3b8") + "55" }}
                                    >
                                        <span>{e.emoji ?? "🐷"}</span>
                                        <span className="text-muted-foreground">{e.name}</span>
                                        <span className="tabular-nums text-xs text-muted-foreground">{formatAmount(e.annual_target_eur, "EUR")} €/год</span>
                                        {e.source === "auto" && <span className="text-[10px] px-1 py-0.5 rounded-full bg-secondary/70 text-muted-foreground" title="Конверт завела сверка по истории">авто</span>}
                                    </Link>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                У этих категорий месячного лимита нет: годовой конверт и месячный лимит вместе считали бы одну трату дважды.
                            </p>
                        </div>
                    )}

                    {/* Категории без лимита */}
                    {unbudgeted.length > 0 && (
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Без лимита</div>
                            <div className="flex flex-wrap gap-2">
                                {unbudgeted.map(c => (
                                    <button
                                        key={c.id}
                                        onClick={() => setModal({ mode: "create-category", categoryId: c.id })}
                                        className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm hover:bg-secondary/40 transition-colors"
                                        style={{ borderColor: (c.color ?? "#94a3b8") + "55" }}
                                    >
                                        <span>{c.emoji ?? "📁"}</span>
                                        <span className="text-muted-foreground">{c.name}</span>
                                        <Plus className="h-3.5 w-3.5 opacity-60" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* SPEC-023: классификация архетипов + override */}
                    {rbarEnabled && <ClassificationSection />}
                </>
            )}

            <BudgetModal state={modal} onClose={() => setModal(null)} unbudgeted={unbudgeted} />
        </div>
    );
}

// ── Total cap card ───────────────────────────────────────────────────────────
function TotalCard({ total, onEdit }: { total: BudgetTotalProgress; onEdit: () => void }) {
    const overBy = total.remaining_eur < 0 ? -total.remaining_eur : 0;
    return (
        <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-9 w-9 rounded-lg grid place-items-center bg-secondary/60 text-foreground shrink-0"><Gauge className="h-5 w-5" /></div>
                    <div className="min-w-0">
                        <div className="font-medium">Общий потолок</div>
                        <div className="text-sm text-muted-foreground">
                            {eur(total.spent_eur)} из {eur(total.limit_eur)}
                            {total.missing_rates > 0 && <span className="text-amber-500"> · {total.missing_rates} без курса</span>}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                        <div className={cn("font-semibold tabular-nums", BUDGET_STATUS_TEXT[total.status])}>
                            {overBy > 0 ? `−${eur(overBy)}` : eur(total.remaining_eur)}
                        </div>
                        <div className="text-xs text-muted-foreground">{overBy > 0 ? "сверх лимита" : "осталось"} · {total.pct}%</div>
                    </div>
                    <button onClick={onEdit} aria-label="Редактировать" className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"><Pencil className="h-4 w-4" /></button>
                </div>
            </div>
            <BudgetBar pct={total.pct} status={total.status} height="h-3" />
        </div>
    );
}

// ── SPEC-058: потолок из плана (read-only) + разбивка ────────────────────────
// Порог истории для масштабирования лимитов — warmup закона SPEC-052 (E8).
const ALLOCATION_WARMUP_MONTHS = 6;

function PlanTotalCard({ total, allocation, ceiling }: {
    total: BudgetTotalProgress; allocation: BudgetAllocation | null; ceiling: PlanCeiling;
}) {
    // Главное число — то же, что на /plan и в Mini App: остаток потолка по регулярным
    // тратам (C − вне конвертов). total.limit = C + отчисления конвертов и считает все траты —
    // это второй, «бухгалтерский» ряд, и главным числом ему быть нельзя (R3 SPEC-057).
    const remaining = ceiling.remaining_eur;
    const limit = ceiling.limit_eur;
    const spent = ceiling.spent_eur;
    const over = remaining < 0;
    const pct = limit > 0 ? Math.round((spent / limit) * 100) : 100;
    const status = over ? "over" : spent >= 0.8 * limit ? "warn" : "good";
    const unallocated = allocation?.unallocated_eur ?? 0;
    const accrual = Math.max(0, total.limit_eur - ceiling.limit_eur);
    const shortHistory = !allocation && ceiling.months_used < ALLOCATION_WARMUP_MONTHS;
    return (
        <div className="card p-4 space-y-3" data-testid="plan-total-card">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-9 w-9 rounded-lg grid place-items-center bg-secondary/60 text-foreground shrink-0"><Target className="h-5 w-5" /></div>
                    <div className="min-w-0">
                        <div className="font-medium flex items-center gap-2">
                            Общий потолок
                            <span className="text-[11px] font-normal px-1.5 py-0.5 rounded-full bg-secondary/70 text-muted-foreground">из плана</span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                            {eur(spent)} из {eur(limit)}
                            {accrual > 0.5 && <span className="opacity-70"> · + конверты {eur(accrual)}</span>}
                            {total.missing_rates > 0 && <span className="text-amber-500"> · {total.missing_rates} без курса</span>}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                        <div className={cn("font-semibold tabular-nums", BUDGET_STATUS_TEXT[status])}>
                            {over ? `−${eur(-remaining)}` : eur(remaining)}
                        </div>
                        <div className="text-xs text-muted-foreground">{over ? "сверх потолка" : "осталось"} · {pct}%</div>
                    </div>
                    <Link to="/plan" aria-label="Открыть план" className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"><ArrowUpRight className="h-4 w-4" /></Link>
                </div>
            </div>
            <BudgetBar pct={pct} status={status} height="h-3" />
            {allocation && (
                <div className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-3 pt-1">
                    <div className="flex justify-between sm:block">
                        <span className="text-muted-foreground">Лимиты категорий</span>
                        <span className="tabular-nums sm:ml-1.5">{eur(allocation.allocated_eur)}</span>
                    </div>
                    <div className="flex justify-between sm:block" title="Траты без категории и категории без лимита: по истории они стоят столько же, сколько остальные, и делят потолок наравне с ними">
                        <span className="text-muted-foreground">Прочее (без лимита)</span>
                        <span className="tabular-nums sm:ml-1.5">{eur(allocation.pool_eur)}</span>
                    </div>
                    <div className="flex justify-between sm:block">
                        {!allocation.feasible ? (
                            <span className="text-destructive">план не сходится · <Link to="/plan" className="underline">каскад</Link></span>
                        ) : Math.abs(unallocated) < 0.5 ? (
                            <span className="text-muted-foreground">распределено полностью</span>
                        ) : unallocated > 0 ? (
                            <><span className="text-muted-foreground">Нераспределено</span><span className="tabular-nums sm:ml-1.5 text-positive">{eur(unallocated)}</span></>
                        ) : (
                            <><span className="text-muted-foreground">Сверх плана</span><span className="tabular-nums sm:ml-1.5 text-destructive">{eur(-unallocated)}</span></>
                        )}
                    </div>
                </div>
            )}
            {shortHistory && (
                <p className="text-xs text-amber-500" data-testid="plan-total-short-history">
                    Мало истории: {ceiling.months_used} из {ALLOCATION_WARMUP_MONTHS} мес — лимиты категорий к потолку пока не масштабируются.
                </p>
            )}
            <p className="text-xs text-muted-foreground">
                Число задаёт цель накоплений на <Link to="/plan" className="underline">/plan</Link>; лимиты категорий — его разбивка, их ставит система после каждого изменения плана и 1-го числа. Закрепить категорию — выключить «адапт.».
            </p>
        </div>
    );
}

// ── Category budget row ──────────────────────────────────────────────────────
function CategoryRow({ b, onEdit }: { b: BudgetCategoryProgress; onEdit: () => void }) {
    const color = b.color ?? "#94a3b8";
    const overBy = b.remaining_eur < 0 ? -b.remaining_eur : 0;
    return (
        <button onClick={onEdit} aria-label={`Редактировать бюджет: ${b.name}`} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/30 transition-colors">
            <div className="h-9 w-9 rounded-lg grid place-items-center text-lg shrink-0" style={{ background: color + "22", color }}>
                {b.emoji ?? "📁"}
            </div>
            <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                    <div className="font-medium truncate">{b.name}</div>
                    <div className="text-sm text-muted-foreground tabular-nums shrink-0">
                        {eur(b.spent_eur)} <span className="opacity-60">/ {eur(b.limit_eur)}</span>
                    </div>
                </div>
                <BudgetBar pct={b.pct} status={b.status} />
                <div className="flex items-center justify-between gap-3 text-xs">
                    <span className={cn("tabular-nums", BUDGET_STATUS_TEXT[b.status])}>
                        {overBy > 0 ? `−${eur(overBy)} сверх` : `осталось ${eur(b.remaining_eur)}`}
                        {b.missing_rates > 0 && <span className="text-amber-500"> · {b.missing_rates} без курса</span>}
                    </span>
                    <span className="text-muted-foreground tabular-nums">{b.pct}%</span>
                </div>
            </div>
            <Pencil className="h-4 w-4 text-muted-foreground shrink-0" />
        </button>
    );
}

// ── Create / edit modal ──────────────────────────────────────────────────────
function BudgetModal({ state, onClose, unbudgeted }: { state: ModalState; onClose: () => void; unbudgeted: Category[] }) {
    const create = useCreateBudget();
    const update = useUpdateBudget();
    const del = useDeleteBudget();
    const toast = useToast();

    const open = state !== null;
    const isEdit = state?.mode === "edit";
    const isTotal = state?.mode === "create-total" || (state?.mode === "edit" && state.isTotal);
    const draftId = useDraftId(open && !isEdit);   // ADM-02 (SPEC-044): id create-записи на одно открытие формы

    const [categoryId, setCategoryId] = useState("");
    const [limit, setLimit] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!state) return;
        setCategoryId(state.mode === "create-category" ? (state.categoryId ?? unbudgeted[0]?.id ?? "") : "");
        setLimit(state.mode === "edit" ? String(state.limit) : "");
        setSubmitting(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.mode, state?.mode === "edit" ? state.budgetId : state?.mode === "create-category" ? state.categoryId : null]);

    const limitNum = parseFloat(limit.replace(",", "."));
    const limitValid = isFinite(limitNum) && limitNum > 0;
    const valid = limitValid && (isEdit || isTotal || categoryId !== "");

    const title = isEdit
        ? (isTotal ? "Общий потолок" : `Бюджет · ${state.label}`)
        : (isTotal ? "Общий месячный потолок" : "Новый бюджет");

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!valid || !state) return;
        setSubmitting(true);
        try {
            if (state.mode === "edit") {
                await update.mutateAsync({ id: state.budgetId, patch: { limit_eur: limitNum } });
            } else if (state.mode === "create-total") {
                await create.mutateAsync({ id: draftId, scope: "total", limit_eur: limitNum });   // ADM-02
            } else {
                await create.mutateAsync({ id: draftId, scope: "category", category_id: categoryId, limit_eur: limitNum });   // ADM-02
            }
            toast("Сохранено", "ok");
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async () => {
        if (!state || state.mode !== "edit") return;
        // ADM-19: единственный delete в приложении был без confirm — выравниваем с остальными.
        if (!confirm(`Удалить лимит «${state.isTotal ? "Общий потолок" : state.label}»?`)) return;
        setSubmitting(true);
        try {
            await del.mutateAsync(state.budgetId);
            toast("Лимит удалён", "ok");
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title={title} size="sm">
            <form onSubmit={submit} className="space-y-5">
                {state?.mode === "create-category" && (
                    <label className="block">
                        <span className="text-sm text-muted-foreground block mb-1.5">Категория</span>
                        <Select fullWidth value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                            {unbudgeted.length === 0 && <option value="">Нет категорий без лимита</option>}
                            {unbudgeted.map(c => (
                                <option key={c.id} value={c.id}>{c.emoji ? `${c.emoji} ` : ""}{c.name}</option>
                            ))}
                        </Select>
                    </label>
                )}

                <label className="block">
                    <span className="text-sm text-muted-foreground block mb-1.5">
                        Месячный лимит, EUR
                    </span>
                    <div className="relative">
                        <input
                            type="text" inputMode="decimal" value={limit} onChange={e => setLimit(e.target.value)} autoFocus
                            placeholder="напр. 300"
                            className="w-full px-3 py-2 pr-9 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
                    </div>
                    {isTotal && (
                        <span className="text-xs text-muted-foreground mt-1.5 block">Лимит на сумму всех трат за календарный месяц.</span>
                    )}
                    {state?.mode === "edit" && state.managedByPlan && (
                        <span className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 block" data-testid="managed-by-plan-hint">
                            Этот лимит задаёт потолок плана: сохранённое число тут же перезапишется сверкой. Чтобы задать лимит самому, выключите «адапт.» у категории в разделе «Как система видит мои категории».
                        </span>
                    )}
                </label>

                <div className="flex justify-between gap-2 pt-2">
                    {isEdit ? (
                        <button type="button" onClick={remove} disabled={submitting}
                            className="btn-ghost px-3 py-2 text-destructive hover:bg-destructive/10">
                            <Trash2 className="h-4 w-4" /> Удалить
                        </button>
                    ) : <span />}
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="btn-ghost px-4 py-2">Отмена</button>
                        <button type="submit" disabled={!valid || submitting} className="btn-primary px-4 py-2 min-w-[7rem]">
                            {submitting ? "…" : (isEdit ? "Сохранить" : "Создать")}
                        </button>
                    </div>
                </div>
            </form>
        </Modal>
    );
}
